import React, { useState, Fragment, useEffect, useCallback } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { withTranslate } from 'react-redux-multilingual'

import {
  fetchCompanyOptions,
  fetchEmpLevelOptions,
  fetchOrganizationOptions
} from '../../../store/actions'
import { importSalary, exportSalary } from '../../../store/importSalary/actions'

import {
  updateFilterData,
  updateFilterOptions,
  updateObject,
  extractFilterOptions,
  getFileFromEvent
} from '../../../helpers'

import FilterModal from '../../../components/FilterModal/FilterModal'
import ImportSalaryContent from '../../../components/PageContent/ImportSalary/ImportSalaryContent'
import Alert from '../../../components/Alert/Alert'

const initialExportFilter = {
  company_id: {
    label: 'company',
    configs: {
      options: []
    },
    value: null
  },
  level_id: {
    label: 'employeeLevel',
    configs: {
      options: []
    },
    value: null
  },
  org_id: {
    label: 'organization',
    configs: {
      options: []
    },
    value: null
  }
}

const filterLabelMapper = {
  company_id: {
    itemKey: 'company_id',
    itemValue: 'company_id',
    itemLabel: { code: 'company_code', name: 'company_name' }
  },
  level_id: {
    itemKey: 'level_id',
    itemValue: 'level_id',
    itemLabel: 'level_name'
  },
  org_id: {
    itemKey: 'org_id',
    itemValue: 'org_id',
    itemLabel: { code: 'org_code', name: 'org_name' }
  }
}

function ImportSalary({ pagePermission: permisison, isAdmin, translate }) {
  const dispatch = useDispatch()

  const exportSuccess = useSelector(state => state.importSalary.exportSuccess)
  const uploadFileProgress = useSelector(state => state.importSalary.uploadFileProgress)
  const loadingQueue = useSelector(state => state.importSalary.loadingStates)
  const companyOptions = useSelector(state => state.importSalary.companies)
  const empLevelOptions = useSelector(state => state.importSalary.empLevels)
  const orgOptions = useSelector(state => state.importSalary.orgs)

  // Import file handler
  const fileChangedHandler = useCallback(
    event => {
      const targetFile = getFileFromEvent({ ...event })?.[0] || null

      if (targetFile) {
        dispatch(importSalary(targetFile))
      }
      event.target.value = null
    },
    [dispatch]
  )

  // Export modal state
  const [exportFilter, setExportFilter] = useState({ ...initialExportFilter })
  const [exportModalOpen, setExportModalOpen] = useState(false)

  const openExportFilterModalHandler = useCallback(() => {
    setExportModalOpen(true)
  }, [])

  const closeExportFilterModalHandler = useCallback(() => {
    setExportModalOpen(false)
  }, [])

  // Filter options changed handler
  const filterOptionChangedHandler = useCallback((itemKey, newOptions, { key, value, label }) => {
    setExportFilter(prevControls => {
      const filterItem = prevControls[itemKey]

      if (!filterItem) {
        return prevControls
      }

      const updatedFilter = updateFilterOptions(newOptions, filterItem, key, value, label)
      return updateObject(prevControls, { [itemKey]: updatedFilter })
    })
  }, [])

  // Filter input changed handler
  const filterInputChangedHandler = useCallback((itemKey, event) => {
    setExportFilter(prevState => {
      const filterItem = prevState[itemKey]
      const updatedForm = updateFilterData(filterItem, event)

      return updateObject(prevState, { [itemKey]: updatedForm })
    })
  }, [])

  // Filter submitted handler
  const filterSubmittedHandler = useCallback(() => {
    const filterOptions = extractFilterOptions(exportFilter)
    console.log(filterOptions)
    dispatch(exportSalary(filterOptions))
  }, [dispatch, exportFilter])

  // Mount Effects
  useEffect(() => {
    dispatch(fetchCompanyOptions())
    dispatch(fetchEmpLevelOptions())
    dispatch(fetchOrganizationOptions())
    return () => {}
  }, [dispatch])

  // Close export modal when export success
  useEffect(() => {
    if (exportSuccess) {
      closeExportFilterModalHandler()
    }
  }, [closeExportFilterModalHandler, exportSuccess])

  useEffect(() => {
    dispatch(fetchEmpLevelOptions({ company_id: exportFilter.company_id.value?.value || null }))
    dispatch(fetchOrganizationOptions({ company_id: exportFilter.company_id.value?.value || null }))
    return () => {}
  }, [dispatch, exportFilter.company_id.value])

  // Company options effect
  useEffect(() => {
    const mapperKey = 'company_id'
    const { itemKey: key, itemValue: value, itemLabel: label } = filterLabelMapper[mapperKey]
    const optionMapper = { key, value, label }

    filterOptionChangedHandler(mapperKey, companyOptions || [], optionMapper)
    return () => {
      filterOptionChangedHandler(mapperKey, [], optionMapper)
    }
  }, [companyOptions, filterOptionChangedHandler])

  // Emp level options effect
  useEffect(() => {
    const mapperKey = 'level_id'
    const { itemKey: key, itemValue: value, itemLabel: label } = filterLabelMapper[mapperKey]
    const optionMapper = { key, value, label }

    filterOptionChangedHandler(key, empLevelOptions || [], optionMapper)
    return () => {
      filterOptionChangedHandler(mapperKey, [], optionMapper)
    }
  }, [empLevelOptions, filterOptionChangedHandler])

  // Org options effect
  useEffect(() => {
    const mapperKey = 'org_id'
    const { itemKey: key, itemValue: value, itemLabel: label } = filterLabelMapper[mapperKey]
    const optionMapper = { key, value, label }

    filterOptionChangedHandler(mapperKey, orgOptions || [], optionMapper)
    return () => {
      filterOptionChangedHandler(mapperKey, [], optionMapper)
    }
  }, [orgOptions, filterOptionChangedHandler])

  return (
    <Fragment>
      <Alert />
      <ImportSalaryContent
        uploadFileProgress={uploadFileProgress}
        fileChanged={fileChangedHandler}
        exportClicked={openExportFilterModalHandler}
        loadingQueue={loadingQueue}
        translate={translate}
      />
      <FilterModal
        open={exportModalOpen}
        header={`${translate('export')} ${translate('salaryData')}`}
        filterControls={exportFilter}
        filterChanged={filterInputChangedHandler}
        filterSaved={filterSubmittedHandler}
        cancelled={closeExportFilterModalHandler}
        loadingQueue={loadingQueue}
      />
    </Fragment>
  )
}

export default withTranslate(ImportSalary)
